import React, { useEffect, useState } from 'react';
import { Alert, Button, ButtonGroup, Col, Container, Form, Row } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import { FaUniversity, FaListAlt, FaFileExcel } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import { salvarEmExcel } from '~/Utils/Helpers';
import {
  listarGEsSaldoBanco,
  listarMoedasSaldoBanco,
  consultarSaldosBanco,
  consultarExtratoCaixa,
} from '~/Services/Service';
import Filtros from './components/Filtros';
import GridSaldos from './components/GridSaldos';
import GridExtratoCaixa from './components/GridExtratoCaixa';

const MODO_SALDOS = 'saldos';
const MODO_EXTRATO = 'extrato';

const saldosSchema = Yup.object().shape({
  codigoMoedaReferencia: Yup.number()
    .nullable()
    .transform((value, originalValue) => {
      if (originalValue === null || originalValue === '' || Number.isNaN(value)) {
        return null;
      }
      return value;
    })
    .required('Selecione a moeda de referência')
    .positive('Moeda inválida'),
  codigosGrupoEmpresa: Yup.array()
    .of(Yup.number())
    .min(1, 'Selecione ao menos um Grupo de Empresa'),
  liquidez: Yup.number().oneOf([0, 1, 2]).required(),
});

const extratoSchema = Yup.object().shape({
  dataInicial: Yup.string()
    .required('Data inicial obrigatória')
    .test('data-valida', 'Data inválida', (v) => moment(v, 'YYYY-MM-DD', true).isValid()),
  dataFinal: Yup.string()
    .required('Data final obrigatória')
    .test('data-valida', 'Data inválida', (v) => moment(v, 'YYYY-MM-DD', true).isValid())
    .test('data-maior', 'Data final deve ser >= data inicial', function validar(v) {
      const { dataInicial } = this.parent;
      if (!dataInicial || !v) return true;
      return moment(v, 'YYYY-MM-DD').isSameOrAfter(moment(dataInicial, 'YYYY-MM-DD'));
    }),
  qualData: Yup.string().oneOf(['N', 'C']).required(),
});

const valoresIniciaisSaldos = {
  codigoMoedaReferencia: null,
  codigosGrupoEmpresa: [],
  liquidez: 0,
  participacaoSocietaria: false,
  cotacoes: {},
  contaEspecifica: null,
};

const valoresIniciaisExtrato = {
  dataInicial: moment().startOf('month').format('YYYY-MM-DD'),
  dataFinal: moment().format('YYYY-MM-DD'),
  qualData: 'N',
  codigoGrupoEmpresa: null,
  contaEspecifica: null,
};

const extrairItens = (res) => {
  const data = res && res.data !== undefined ? res.data : res;
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.itens)) return data.itens;
  if (data && Array.isArray(data.Itens)) return data.Itens;
  return [];
};

const SaldoContaBanco = () => {
  const [modo, setModo] = useState(MODO_SALDOS);
  const [grupos, setGrupos] = useState([]);
  const [moedas, setMoedas] = useState([]);
  const [carregando, setCarregando] = useState(true);

  const [resultadoSaldos, setResultadoSaldos] = useState(null);
  const [resultadoExtrato, setResultadoExtrato] = useState(null);
  const [consultando, setConsultando] = useState(false);
  const [exportandoExcel, setExportandoExcel] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    let ativo = true;

    const carregar = async () => {
      try {
        const [resGes, resMoedas] = await Promise.all([
          listarGEsSaldoBanco(),
          listarMoedasSaldoBanco(),
        ]);
        if (!ativo) return;
        const listaGes = Array.isArray(resGes) ? resGes : (resGes && resGes.data ? resGes.data : []);
        const listaMoedas = Array.isArray(resMoedas) ? resMoedas : (resMoedas && resMoedas.data ? resMoedas.data : []);
        setGrupos(listaGes);
        setMoedas(listaMoedas);
      } catch (err) {
        if (!ativo) return;
        const msg = err && err.message ? err.message : 'Erro ao carregar dados.';
        toast.error(msg);
      } finally {
        if (ativo) setCarregando(false);
      }
    };

    carregar();
    return () => { ativo = false; };
  }, []);

  const nomesMoedas = moedas.reduce((acc, m) => {
    acc[m.codigo_ativo] = m.nome;
    return acc;
  }, {});

  const handleConsultarSaldos = async (values) => {
    setErro('');
    setResultadoSaldos(null);
    setConsultando(true);

    const payload = {
      codigo_moeda_referencia: values.codigoMoedaReferencia,
      codigos_grupo_empresa: values.codigosGrupoEmpresa,
      liquidez: values.liquidez,
      participacao_societaria: values.participacaoSocietaria,
      cotacoes: values.cotacoes,
      conta_especifica: values.contaEspecifica,
    };

    try {
      const res = await consultarSaldosBanco(payload);
      const dados = extrairItens(res);
      setResultadoSaldos(dados);
      if (dados.length === 0) setErro('Nenhum saldo encontrado para os filtros informados.');
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao consultar saldos.';
      setErro(msg);
      toast.error(msg);
    } finally {
      setConsultando(false);
    }
  };

  const handleConsultarExtrato = async (values) => {
    setErro('');
    setResultadoExtrato(null);
    setConsultando(true);

    const payload = {
      data_inicial: values.dataInicial,
      data_final: values.dataFinal,
      qual_data: values.qualData,
      codigo_grupo_empresa: values.codigoGrupoEmpresa,
      conta_especifica: values.contaEspecifica,
    };

    try {
      const res = await consultarExtratoCaixa(payload);
      const dados = extrairItens(res);
      setResultadoExtrato(dados);
      if (dados.length === 0) setErro('Nenhum lançamento encontrado para o período informado.');
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao consultar extrato de caixa.';
      setErro(msg);
      toast.error(msg);
    } finally {
      setConsultando(false);
    }
  };

  const handleAlterarModo = (novoModo) => {
    setModo(novoModo);
    setErro('');
    setResultadoSaldos(null);
    setResultadoExtrato(null);
  };

  const handleExportarSaldos = async () => {
    if (!resultadoSaldos || resultadoSaldos.length === 0) return;
    setExportandoExcel(true);
    try {
      await salvarEmExcel(resultadoSaldos, '/exportador/excel/saldo-conta-banco-saldos');
    } catch (err) {
      toast.error(err && err.message ? err.message : 'Erro ao exportar saldos.');
    } finally {
      setExportandoExcel(false);
    }
  };

  const handleExportarExtrato = async () => {
    if (!resultadoExtrato || resultadoExtrato.length === 0) return;
    setExportandoExcel(true);
    try {
      await salvarEmExcel(resultadoExtrato, '/exportador/excel/saldo-conta-banco-extrato');
    } catch (err) {
      toast.error(err && err.message ? err.message : 'Erro ao exportar extrato.');
    } finally {
      setExportandoExcel(false);
    }
  };

  if (carregando) {
    return (
      <div>
        <Header title="Saldo de Conta Banco" />
        <Loading />
      </div>
    );
  }

  return (
    <div>
      <Header title="Saldo de Conta Banco" />
      <Container fluid className="py-3">
        <Row className="mb-3">
          <Col xs={12}>
            <ButtonGroup>
              <Button
                variant={modo === MODO_SALDOS ? 'primary' : 'outline-primary'}
                onClick={() => handleAlterarModo(MODO_SALDOS)}
              >
                <FaUniversity className="mr-2" />
                Saldos
              </Button>
              <Button
                variant={modo === MODO_EXTRATO ? 'primary' : 'outline-primary'}
                onClick={() => handleAlterarModo(MODO_EXTRATO)}
              >
                <FaListAlt className="mr-2" />
                Extrato Caixa
              </Button>
            </ButtonGroup>
          </Col>
        </Row>

        {modo === MODO_SALDOS && (
          <Formik
            initialValues={valoresIniciaisSaldos}
            validationSchema={saldosSchema}
            onSubmit={handleConsultarSaldos}
          >
            {({ values, errors, touched, setFieldValue, handleChange, handleBlur, handleSubmit, isValid }) => (
              <Form onSubmit={handleSubmit}>
                <Filtros
                  modo={MODO_SALDOS}
                  values={values}
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  grupos={grupos}
                  moedas={moedas}
                />

                {erro && (
                  <Alert variant="warning" className="py-2 small">
                    {erro}
                  </Alert>
                )}

                <Row className="mb-3">
                  <Col xs={12}>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={consultando || !isValid}
                    >
                      {consultando ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm mr-2"
                            role="status"
                            aria-hidden="true"
                          />
                          Consultando...
                        </>
                      ) : (
                        <>
                          <FaUniversity className="mr-2" />
                          Consultar Saldos
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline-success"
                      className="ml-2"
                      onClick={handleExportarSaldos}
                      disabled={exportandoExcel || !resultadoSaldos || resultadoSaldos.length === 0}
                    >
                      <FaFileExcel className="mr-2" />
                      Exportar Excel
                    </Button>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>
        )}

        {modo === MODO_EXTRATO && (
          <Formik
            initialValues={valoresIniciaisExtrato}
            validationSchema={extratoSchema}
            onSubmit={handleConsultarExtrato}
          >
            {({ values, errors, touched, setFieldValue, handleChange, handleBlur, handleSubmit, isValid }) => (
              <Form onSubmit={handleSubmit}>
                <Filtros
                  modo={MODO_EXTRATO}
                  values={values}
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  grupos={grupos}
                  moedas={moedas}
                />

                {erro && (
                  <Alert variant="warning" className="py-2 small">
                    {erro}
                  </Alert>
                )}

                <Row className="mb-3">
                  <Col xs={12}>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={consultando || !isValid}
                    >
                      {consultando ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm mr-2"
                            role="status"
                            aria-hidden="true"
                          />
                          Consultando...
                        </>
                      ) : (
                        <>
                          <FaListAlt className="mr-2" />
                          Consultar Extrato
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline-success"
                      className="ml-2"
                      onClick={handleExportarExtrato}
                      disabled={exportandoExcel || !resultadoExtrato || resultadoExtrato.length === 0}
                    >
                      <FaFileExcel className="mr-2" />
                      Exportar Excel
                    </Button>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>
        )}

        {resultadoSaldos !== null && (
          <GridSaldos dados={resultadoSaldos} nomesMoedas={nomesMoedas} />
        )}

        {resultadoExtrato !== null && (
          <GridExtratoCaixa dados={resultadoExtrato} />
        )}
      </Container>
    </div>
  );
};

export default SaldoContaBanco;
